import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Smartphone, ExternalLink, Copy, Wallet, Gift } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";

export default function Checkout() {
  const { items, subtotal, cloneQty, clearCart, hasPreorder, hasReady } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [promo, setPromo] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoPercent, setPromoPercent] = useState(0);
  const [promoCode, setPromoCode] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cashapp");

  const [creditBalance, setCreditBalance] = useState(0);
  const [maxRedeemPct, setMaxRedeemPct] = useState(50);
  const [creditInput, setCreditInput] = useState("");
  const [creditApplied, setCreditApplied] = useState(0);
  const [referralCode, setReferralCode] = useState("");
  const [welcomeCredit, setWelcomeCredit] = useState(10);
  const [referralReward, setReferralReward] = useState(25);

  useEffect(() => {
    if (!user) return;
    api.get("/rewards/me")
      .then(({ data }) => {
        setCreditBalance(data.balance);
        setMaxRedeemPct(data.max_redemption_percent);
        setReferralCode(data.referral_code || "");
        if (data.welcome_credit != null) setWelcomeCredit(data.welcome_credit);
        if (data.referral_reward != null) setReferralReward(data.referral_reward);
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user?.name) setShipping((s) => (s.full_name ? s : { ...s, full_name: user.name }));
  }, [user]);

  const [shipping, setShipping] = useState({
    full_name: user?.name || "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    phone: "",
  });

  const shippingFee = 0; // Free next-day shipping on all orders
  const discount = promoApplied ? Math.round(subtotal * promoPercent * 100) / 10000 : 0;
  const discountedSubtotal = Math.round((subtotal - discount) * 100) / 100;
  const maxRedeemable = Math.max(0, Math.round(Math.min(creditBalance, discountedSubtotal * maxRedeemPct / 100) * 100) / 100);
  const total = Math.max(0, Math.round((discountedSubtotal - creditApplied + shippingFee) * 100) / 100);

  const applyCredit = () => {
    const val = Math.round(Number(creditInput) * 100) / 100;
    if (!val || val <= 0) return;
    const applied = Math.min(val, maxRedeemable);
    setCreditApplied(applied);
    if (applied < val) toast.info(`Applied $${applied.toFixed(2)} — max ${maxRedeemPct}% of order can be paid with credit.`);
    else toast.success(`$${applied.toFixed(2)} credit applied.`);
  };

  const applyMaxCredit = () => {
    setCreditApplied(maxRedeemable);
    setCreditInput(String(maxRedeemable));
  };

  const removeCredit = () => { setCreditApplied(0); setCreditInput(""); };

  const applyPromo = async () => {
    const code = promo.trim();
    if (!code) return;
    try {
      const { data } = await api.post("/promos/validate", {
        code,
        clone_qty: cloneQty,
        subtotal,
      });
      if (!data.valid) {
        setPromoApplied(false);
        toast.error(data.message || "Invalid promo code.");
        return;
      }
      setPromoApplied(true);
      setPromoPercent(data.discount_percent);
      setPromoCode(data.code);
      toast.success(`Promo code ${data.code} applied — ${data.discount_percent}% off!`);
    } catch (err) {
      setPromoApplied(false);
      toast.error(formatApiError(err.response?.data?.detail) || "Could not validate code.");
    }
  };

  const removePromo = () => {
    setPromoApplied(false);
    setPromoPercent(0);
    setPromoCode("");
    setPromo("");
  };

  const set = (obj, key) => (e) => obj[1]({ ...obj[0], [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please log in to complete your order.");
      navigate("/login?redirect=/checkout");
      return;
    }
    if (items.length === 0) {
      toast.error("Your cart is empty.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items,
        shipping,
        promo_code: promoApplied ? promoCode : null,
        apply_credit: creditApplied > 0 ? creditApplied : 0,
        payment_method: paymentMethod,
      });
      clearCart();
      setDone(data);
      toast.success(paymentMethod === "venmo" ? "Order placed — complete your Venmo payment." : "Order placed — complete your Cash App payment.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not place order.");
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div className="max-w-xl mx-auto px-6 py-20 text-center" data-testid="order-success">
        <Seo title="Complete Your Payment | Dark Growers Genetics" noindex />
        <CheckCircle2 size={52} strokeWidth={1.2} className="text-emerald-500 mx-auto mb-5" />
        <h1 className="font-display text-4xl sm:text-5xl text-slate-100">Order Placed</h1>
        <p className="text-slate-400 mt-4 text-sm">
          Your order <span className="text-emerald-400">{done.invoice}</span> is reserved. Complete
          your payment with {done.method === "venmo" ? "Venmo" : "Cash App"} below to lock it in for free next-day shipping.
        </p>

        {done.method === "venmo" ? (
        <div className="border border-[#3d95ce]/40 bg-[#3d95ce]/[0.08] p-7 mt-8" data-testid="venmo-block">
          <p className="label-caps text-[#5fb0e6] mb-1 flex items-center justify-center gap-2">
            <Smartphone size={14} /> Pay with Venmo
          </p>
          <p className="text-slate-400 text-xs mb-5">Send the exact total to the Venmo handle below.</p>

          <div className="mt-1 space-y-1">
            <p className="font-display text-3xl text-slate-100" data-testid="venmo-handle">{done.venmo_handle}</p>
            <p className="text-[#5fb0e6] text-2xl font-semibold">${done.total.toFixed(2)}</p>
          </div>

          <div className="mt-5 flex flex-col items-center gap-2 text-xs">
            <span className="text-slate-400">Venmo note — enter this only:</span>
            <div className="flex items-center gap-2">
              <code className="bg-black/50 border border-white/10 px-2 py-1 text-[#5fb0e6] text-sm" data-testid="venmo-note">{done.invoice}</code>
              <button
                type="button"
                data-testid="copy-invoice"
                onClick={() => { navigator.clipboard?.writeText(done.invoice); toast.success("Order number copied"); }}
                className="text-slate-400 hover:text-[#5fb0e6]"
                title="Copy order number"
              >
                <Copy size={13} />
              </button>
            </div>
          </div>

          <div className="mt-4 border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-left">
            <p className="text-amber-300 text-xs font-semibold uppercase tracking-wide mb-1">Important</p>
            <p className="text-amber-100/90 text-xs leading-relaxed">
              Put your <b>ORDER NUMBER ONLY</b> in the Venmo note. <b>Do not mention cannabis,
              seeds, clones, or any product</b> in the memo — order number only.
            </p>
          </div>

          <a
            href={done.venmo_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="venmo-pay-button"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#3d95ce] text-white hover:bg-[#4fa5de] transition-colors font-semibold py-3.5 text-sm tracking-wide"
          >
            Pay {done.venmo_handle} on Venmo <ExternalLink size={15} />
          </a>
          <p className="text-[0.7rem] text-slate-500 mt-3 leading-relaxed">
            Your order status is <span className="text-amber-400">Pending Payment</span>. Once your
            payment is completed and your order ships, you'll receive an email notification with
            tracking.
          </p>
        </div>
        ) : (
        <div className="border border-emerald-500/30 bg-emerald-500/[0.06] p-7 mt-8" data-testid="cashapp-block">
          <p className="label-caps text-emerald-400 mb-1 flex items-center justify-center gap-2">
            <Smartphone size={14} /> Pay with Cash App
          </p>
          <p className="text-slate-400 text-xs mb-5">Send the exact total to the Cashtag below.</p>

          <img
            data-testid="cashapp-qr"
            src="/cashapp-qr.png"
            alt="Cash App QR code for $darkgrower"
            className="w-48 h-48 mx-auto rounded-lg bg-white p-1"
          />

          <div className="mt-5 space-y-1">
            <p className="font-display text-3xl text-slate-100" data-testid="cashapp-tag">{done.cashtag}</p>
            <p className="text-emerald-400 text-2xl font-semibold">${done.total.toFixed(2)}</p>
          </div>

          <div className="mt-5 flex flex-col items-center gap-2 text-xs">
            <span className="text-slate-400">Cash App note / memo — enter this only:</span>
            <div className="flex items-center gap-2">
              <code className="bg-black/50 border border-white/10 px-2 py-1 text-emerald-300 text-sm" data-testid="cashapp-note">{done.invoice}</code>
              <button
                type="button"
                data-testid="copy-invoice"
                onClick={() => { navigator.clipboard?.writeText(done.invoice); toast.success("Order number copied"); }}
                className="text-slate-400 hover:text-emerald-400"
                title="Copy order number"
              >
                <Copy size={13} />
              </button>
            </div>
          </div>

          <div className="mt-4 border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-left">
            <p className="text-amber-300 text-xs font-semibold uppercase tracking-wide mb-1">Important</p>
            <p className="text-amber-100/90 text-xs leading-relaxed">
              Put your <b>ORDER NUMBER ONLY</b> in the Cash App note. <b>Do not mention cannabis,
              seeds, clones, or any product</b> in the memo — order number only.
            </p>
          </div>

          <a
            href={done.cashapp_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cashapp-pay-button"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-emerald-500 text-[#050505] hover:bg-emerald-400 transition-colors font-semibold py-3.5 text-sm tracking-wide"
          >
            Pay {done.cashtag} on Cash App <ExternalLink size={15} />
          </a>
          <p className="text-[0.7rem] text-slate-500 mt-3 leading-relaxed">
            Your order status is <span className="text-amber-400">Pending Payment</span>. Once your
            payment is completed and your order ships, you'll receive an email notification with
            tracking.
          </p>
        </div>
        )}

        <div className="flex gap-3 justify-center mt-8">
          <Link to="/account" className="bg-white/10 text-slate-100 hover:bg-white/20 transition-colors px-8 py-3 text-sm">View Orders</Link>
          <Link to="/shop" className="border border-white/20 text-slate-300 hover:text-white px-8 py-3 text-sm">Keep Shopping</Link>
        </div>

        {referralCode && (
          <div className="border border-emerald-500/30 bg-emerald-500/[0.06] p-7 mt-8 text-left" data-testid="referral-cta">
            <p className="label-caps text-emerald-400 mb-1 flex items-center gap-2 justify-center"><Gift size={15} /> Give ${welcomeCredit.toFixed(0)}, get ${referralReward.toFixed(0)} in store credit</p>
            <p className="text-slate-400 text-xs text-center mb-4">Share your link — your friend gets <b className="text-emerald-400">${welcomeCredit.toFixed(0)} in store credit</b> toward their first order, and you earn <b className="text-emerald-400">${referralReward.toFixed(0)} in store credit</b> when they complete it. Store credit only — no cash value.</p>
            <div className="flex gap-2">
              <input
                readOnly
                data-testid="referral-cta-link"
                value={`${window.location.origin}/?ref=${referralCode}`}
                className="flex-1 bg-[#050505] border border-white/10 text-xs text-slate-300 px-3 py-2.5 outline-none"
              />
              <button
                type="button"
                data-testid="referral-cta-copy"
                onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/?ref=${referralCode}`); toast.success("Referral link copied!"); }}
                className="inline-flex items-center gap-2 bg-emerald-600 text-white hover:bg-emerald-500 transition-colors px-4 py-2.5 text-sm"
              >
                <Copy size={14} /> Copy
              </button>
            </div>
          </div>
        )}
      </div>
    );

  const inputCls =
    "w-full bg-[#050505] border border-white/10 focus:border-emerald-500 outline-none text-sm text-slate-200 px-4 py-3 transition-colors placeholder:text-slate-600";

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16">
      <Seo title="Checkout | Dark Growers Genetics" noindex />
      <h1 className="font-display text-5xl text-slate-100 mb-10">Checkout</h1>

      {items.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-slate-500">Your cart is empty.</p>
          <Link to="/shop" className="text-emerald-400 mt-4 inline-block">Browse genetics</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-10">
            <div>
              <h2 className="label-caps text-emerald-500 mb-5">Shipping Details</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <input required data-testid="ship-name" placeholder="Full name" className={`${inputCls} sm:col-span-2`} value={shipping.full_name} onChange={set([shipping, setShipping], "full_name")} />
                <input required data-testid="ship-address" placeholder="Street address" className={`${inputCls} sm:col-span-2`} value={shipping.address} onChange={set([shipping, setShipping], "address")} />
                <input required data-testid="ship-city" placeholder="City" className={inputCls} value={shipping.city} onChange={set([shipping, setShipping], "city")} />
                <input required data-testid="ship-state" placeholder="State" className={inputCls} value={shipping.state} onChange={set([shipping, setShipping], "state")} />
                <input required data-testid="ship-zip" placeholder="ZIP code" className={inputCls} value={shipping.zip_code} onChange={set([shipping, setShipping], "zip_code")} />
                <input data-testid="ship-phone" placeholder="Phone (optional)" className={inputCls} value={shipping.phone} onChange={set([shipping, setShipping], "phone")} />
              </div>
            </div>

            <div>
              <h2 className="label-caps text-emerald-500 mb-3 flex items-center gap-2">
                <Smartphone size={14} /> Payment Method
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  data-testid="pay-method-cashapp"
                  onClick={() => setPaymentMethod("cashapp")}
                  className={`border p-4 text-left transition-colors ${paymentMethod === "cashapp" ? "border-emerald-500 bg-emerald-500/[0.08]" : "border-white/10 bg-[#0F0F0F] hover:border-white/25"}`}
                >
                  <span className={`block text-sm font-semibold ${paymentMethod === "cashapp" ? "text-emerald-400" : "text-slate-200"}`}>Cash App</span>
                  <span className="block text-xs text-slate-500 mt-1">Pay $darkgrower</span>
                </button>
                <button
                  type="button"
                  data-testid="pay-method-venmo"
                  onClick={() => setPaymentMethod("venmo")}
                  className={`border p-4 text-left transition-colors ${paymentMethod === "venmo" ? "border-[#3d95ce] bg-[#3d95ce]/[0.1]" : "border-white/10 bg-[#0F0F0F] hover:border-white/25"}`}
                >
                  <span className={`block text-sm font-semibold ${paymentMethod === "venmo" ? "text-[#5fb0e6]" : "text-slate-200"}`}>Venmo</span>
                  <span className="block text-xs text-slate-500 mt-1">Pay @DarkGrowers</span>
                </button>
              </div>
              <div className="border border-white/10 bg-[#0F0F0F] p-5 text-sm text-slate-400 leading-relaxed mt-3">
                Place your order below, then pay securely with{" "}
                {paymentMethod === "venmo" ? (
                  <><span className="text-slate-200">Venmo</span> to <span className="text-[#5fb0e6] font-medium">@DarkGrowers</span></>
                ) : (
                  <><span className="text-slate-200">Cash App</span> to <span className="text-emerald-400 font-medium">$darkgrower</span></>
                )}
                . After you submit, we'll show a payment button and email you the details. Your order ships free
                next-day as soon as we confirm payment.
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="border border-white/10 bg-[#0F0F0F] p-6 sticky top-28">
              <h2 className="font-display text-2xl text-slate-100 mb-5">Order Summary</h2>
              <div className="space-y-4 max-h-60 overflow-y-auto pr-1">
                {items.map((i) => (
                  <div key={i.product_id} className="flex gap-3 text-sm">
                    <img src={i.image_url} alt="" loading="lazy" decoding="async" className="w-12 h-12 object-cover bg-black border border-white/5" />
                    <div className="flex-1">
                      <p className="text-slate-200 leading-tight">{i.name}</p>
                      <p className="text-slate-500 text-xs">Qty {i.quantity}</p>
                      {i.availability === "preorder" && (
                        <p className="text-[0.7rem] text-amber-400 mt-0.5">Made to order — roots in ~2 weeks, then ships next day</p>
                      )}
                    </div>
                    <span className="text-slate-300">${(i.price * i.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {hasPreorder && hasReady && (
                <p data-testid="checkout-mixed-note" className="text-xs text-amber-400 border border-amber-500/30 bg-amber-500/10 px-3 py-2.5 mt-4">
                  Your order ships together once all cuts are rooted.
                </p>
              )}

              <div className="border-t border-white/10 mt-5 pt-5">
                <label className="label-caps text-slate-400 text-xs">Promo Code</label>
                <div className="flex gap-2 mt-2">
                  <input
                    data-testid="promo-input"
                    placeholder="Enter code"
                    value={promo}
                    onChange={(e) => setPromo(e.target.value)}
                    disabled={promoApplied}
                    className="flex-1 bg-[#050505] border border-white/10 focus:border-emerald-500 outline-none text-sm text-slate-200 px-3 py-2.5 uppercase tracking-wide transition-colors placeholder:text-slate-600 disabled:opacity-60"
                  />
                  {promoApplied ? (
                    <button
                      type="button"
                      data-testid="promo-remove"
                      onClick={removePromo}
                      className="border border-white/20 text-slate-300 hover:text-white px-4 py-2.5 text-sm"
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="promo-apply"
                      onClick={applyPromo}
                      className="bg-white/10 text-slate-100 hover:bg-white/20 px-4 py-2.5 text-sm transition-colors"
                    >
                      Apply
                    </button>
                  )}
                </div>
                {promoApplied && (
                  <p className="text-xs text-emerald-400 mt-2" data-testid="promo-success">Code {promoCode} applied — {promoPercent}% off your order.</p>
                )}
              </div>

              {user && creditBalance > 0 && (
                <div className="border-t border-white/10 mt-5 pt-5" data-testid="credit-section">
                  <label className="label-caps text-slate-400 text-xs flex items-center gap-1.5">
                    <Wallet size={13} className="text-emerald-400" /> Store Credit
                  </label>
                  <p className="text-xs text-slate-500 mt-1">
                    Balance: <span className="text-emerald-400" data-testid="credit-balance">${creditBalance.toFixed(2)}</span>
                    {maxRedeemable > 0 && <> · up to <span className="text-slate-300">${maxRedeemable.toFixed(2)}</span> on this order</>}
                  </p>
                  <div className="flex gap-2 mt-2">
                    <input
                      data-testid="credit-input"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={creditInput}
                      onChange={(e) => setCreditInput(e.target.value)}
                      disabled={creditApplied > 0 || maxRedeemable <= 0}
                      className="flex-1 bg-[#050505] border border-white/10 focus:border-emerald-500 outline-none text-sm text-slate-200 px-3 py-2.5 transition-colors placeholder:text-slate-600 disabled:opacity-60"
                    />
                    {creditApplied > 0 ? (
                      <button type="button" data-testid="credit-remove" onClick={removeCredit} className="border border-white/20 text-slate-300 hover:text-white px-4 py-2.5 text-sm">Remove</button>
                    ) : (
                      <>
                        <button type="button" data-testid="credit-apply" onClick={applyCredit} disabled={maxRedeemable <= 0} className="bg-white/10 text-slate-100 hover:bg-white/20 px-4 py-2.5 text-sm transition-colors disabled:opacity-50">Apply</button>
                        <button type="button" data-testid="credit-max" onClick={applyMaxCredit} disabled={maxRedeemable <= 0} className="border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 px-3 py-2.5 text-xs transition-colors disabled:opacity-50">Max</button>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className="border-t border-white/10 mt-5 pt-5 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="text-slate-200">${subtotal.toFixed(2)}</span></div>
                {discount > 0 && (
                  <div className="flex justify-between" data-testid="checkout-discount">
                    <span className="text-emerald-400">Promo {promoCode} ({promoPercent}% off)</span>
                    <span className="text-emerald-400">-${discount.toFixed(2)}</span>
                  </div>
                )}
                {creditApplied > 0 && (
                  <div className="flex justify-between" data-testid="checkout-credit">
                    <span className="text-emerald-400">Store credit</span>
                    <span className="text-emerald-400">-${creditApplied.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span className="text-emerald-400">Free · Next-Day</span></div>
                <div className="flex justify-between text-base pt-2 border-t border-white/10 mt-2"><span className="text-slate-100">Total</span><span data-testid="checkout-total" className="text-emerald-400">${total.toFixed(2)}</span></div>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="place-order-button"
                className="w-full bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 transition-colors font-medium py-3.5 text-sm tracking-wide mt-6"
              >
                {loading ? "Placing order..." : `Place Order · $${total.toFixed(2)}`}
              </button>
              {!user && (
                <p className="text-xs text-amber-400/80 mt-3 text-center">
                  You'll need to <Link to="/login?redirect=/checkout" className="underline">log in</Link> to order.
                </p>
              )}
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
